import axios, {AxiosRequestConfig, AxiosResponse} from 'axios';
import stringify from 'fast-json-stable-stringify';
import {get} from 'lodash-es';
import {Store} from 'redux';
import {v4, validate as validateUuid} from 'uuid';

import {cleanObject} from '../../utils/utilities';
import * as enums from '../enums';

const USER_REQUEST_ID_HEADER = 'userRequestId';
const AXIOS_HEADER_BUCKETS = [
    'common',
    'delete',
    'get',
    'head',
    'post',
    'put',
    'patch'
] as const;

function isUserRequestIdKey(key: string): boolean {
    return key.toLowerCase() === USER_REQUEST_ID_HEADER.toLowerCase();
}

function readValidUuid(value: unknown): string | undefined {
    if (typeof value !== 'string') {
        return undefined;
    }
    const candidate = value.trim();
    // Reject comma-joined chains and any non-UUID.
    if (candidate.includes(',') || !validateUuid(candidate)) {
        return undefined;
    }
    return candidate;
}

function collectHeaderObjects(
    headers: AxiosRequestConfig['headers'] | undefined
): Record<string, unknown>[] {
    if (!headers || typeof headers !== 'object') {
        return [];
    }

    const objects: Record<string, unknown>[] = [];
    for (const bucket of AXIOS_HEADER_BUCKETS) {
        const nested = (headers as Record<string, unknown>)[bucket];
        if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
            objects.push(nested as Record<string, unknown>);
        }
    }
    objects.push(headers as Record<string, unknown>);
    return objects;
}

/**
 * Resolve exactly one canonical userRequestId for a request.
 *
 * Business invariant: a UI-initiated trade action emits one UUID per action, and
 * every outbound API call carries exactly one header value (never a comma chain).
 * MULTI-EDIT may reuse one valid UUID across fan-out requests; malformed values
 * (chains, case-duplicate leftovers, non-UUIDs) are discarded and replaced.
 */
export function resolveUserRequestId(
    headers: AxiosRequestConfig['headers'] | undefined
): string {
    const headerObjects = collectHeaderObjects(headers);

    // Prefer the exact canonical key used by MULTI-EDIT / useResource.
    for (let i = headerObjects.length - 1; i >= 0; i -= 1) {
        const canonical = readValidUuid(headerObjects[i][USER_REQUEST_ID_HEADER]);
        if (canonical) {
            return canonical;
        }
    }

    const uniqueValid = new Set<string>();
    for (const headerObject of headerObjects) {
        for (const [key, entry] of Object.entries(headerObject)) {
            if (!isUserRequestIdKey(key)) {
                continue;
            }
            const valid = readValidUuid(entry);
            if (valid) {
                uniqueValid.add(valid);
            }
        }
    }

    // Ambiguous case variants with different UUIDs are treated as malformed.
    if (uniqueValid.size === 1) {
        return [...uniqueValid][0];
    }

    return v4();
}

function stripUserRequestIdKeys(target: Record<string, unknown>): void {
    for (const key of Object.keys(target)) {
        if (isUserRequestIdKey(key)) {
            delete target[key];
        }
    }
}

function stripUserRequestIdHeaders(
    headers: AxiosRequestConfig['headers'] | undefined
): Record<string, unknown> {
    const next: Record<string, unknown> = {
        ...((headers as Record<string, unknown>) || {})
    };

    stripUserRequestIdKeys(next);
    for (const bucket of AXIOS_HEADER_BUCKETS) {
        const nested = next[bucket];
        if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
            const cloned = {...(nested as Record<string, unknown>)};
            stripUserRequestIdKeys(cloned);
            next[bucket] = cloned;
        }
    }

    return next;
}

export async function cdmmwRequestInterceptor(
    store: Store,
    config: AxiosRequestConfig & {isPillarSource?: boolean}
): Promise<AxiosRequestConfig | AxiosResponse> {
    const state = store.getState();

    const micIsUp = state.pillarStatus[state.platform.mic];
    const user = state.user;
    const entitlement =
        state.user.entitlement === 'SUPER_USER'
            ? state.entitlement.name || 'MEMBER_FIRM'
            : state.user.entitlement;

    const mic = get(state, 'route.url', '') === '/system/broadcast-message' ? ''
        : (get(config, 'params.platform', undefined) || state.platform.mic);

    if (user.isJwtThreshold && !config.url.includes('/auth/refreshToken')) {
        throw new axios.Cancel(enums.AUTH_THRESHOLD);
    }

    if (config.isPillarSource && !micIsUp) {
        throw new axios.Cancel(enums.PILLAR_OFFLINE);
    }

    // @ts-ignore
    if (window.MOCK_MODE) {
        config.headers['Mock-Mode'] = true;
        //eslint-disable-next-line no-console
        console.warn('[Mock Mode Enable] the API::', config.url);
    }

    const cleanedHeaders = stripUserRequestIdHeaders(config.headers);
    const userRequestId = resolveUserRequestId(config.headers);

    return {
        ...config,
        headers: {
            ...cleanedHeaders,
            [USER_REQUEST_ID_HEADER]: userRequestId,
            Authorization: stringify(
                cleanObject({
                    accessToken: state.user.sentinel.accessToken,
                    mic,
                    entitlement
                })
            )
        }
    };
}
